import { Check, X, Mail, AlertTriangle } from "lucide-react";
import LegalLayout from "@/pages/LegalLayout";

export default function Refund() {
  return (
    <LegalLayout
      title="Refund Policy"
      effective="March 2, 2026"
      testId="refund-page"
    >
      <div className="bg-[#121316] border border-[#F04632]/30 rounded-2xl p-6 md:p-8 mb-8">
        <p className="text-xs uppercase tracking-[0.25em] text-[#FF7A66] font-bold mb-3">
          Product this policy applies to
        </p>
        <p className="text-white font-bold text-xl leading-tight">
          Plan of Action — $197 (one-time custom digital work product)
        </p>
      </div>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-4 mb-4">
          Plan of Action — Refund Policy ($197)
        </h2>
        <p>
          The Plan of Action is a{" "}
          <strong className="text-white">custom digital work product</strong> created from
          the specific details you provide about your account. Because of this, it is{" "}
          <strong className="text-white">non-refundable once it has been generated and
          delivered to you.</strong>
        </p>
        <p className="mt-4">
          If you have purchased but have{" "}
          <strong className="text-white">not yet submitted your case details or triggered
          generation</strong>, you may request a full refund by emailing{" "}
          <a
            href="mailto:hello@protegoos.com"
            className="text-[#F04632] font-semibold hover:underline"
          >
            hello@protegoos.com
          </a>
          .
        </p>
        <p className="mt-4">
          We do not guarantee reinstatement or any specific outcome. The Plan of Action is a
          strategic document; Amazon&apos;s enforcement and reinstatement decisions are made
          solely by Amazon and are outside our control.{" "}
          <strong className="text-white">Reinstatement is not a condition of this purchase.</strong>
        </p>
        <p className="mt-4">
          Refund requests that fall outside this policy are reviewed case by case at our
          discretion.
        </p>
      </section>

      {/* Visual eligibility breakdown */}
      <section className="mt-12">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-6">
          At a glance
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-[#121316] border border-[#10B981]/30 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 border border-[#10B981]/40 flex items-center justify-center">
                <Check className="w-5 h-5 text-[#10B981]" strokeWidth={3} />
              </div>
              <p className="text-lg font-black text-white tracking-tight">
                Eligible for refund
              </p>
            </div>
            <ul className="space-y-2 text-white/75 text-sm sm:text-base">
              <li>You purchased but have not yet submitted case details.</li>
              <li>You have not triggered generation of the Plan of Action.</li>
              <li>
                You email{" "}
                <span className="font-mono text-white">hello@protegoos.com</span> requesting a
                refund.
              </li>
            </ul>
          </div>

          <div className="bg-[#121316] border border-[#F04632]/30 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#F04632]/10 border border-[#F04632]/40 flex items-center justify-center">
                <X className="w-5 h-5 text-[#F04632]" strokeWidth={3} />
              </div>
              <p className="text-lg font-black text-white tracking-tight">
                Not eligible for refund
              </p>
            </div>
            <ul className="space-y-2 text-white/75 text-sm sm:text-base">
              <li>The Plan of Action has already been generated and delivered to you.</li>
              <li>You have submitted your case details or triggered generation.</li>
              <li>
                Amazon rejects your appeal (reinstatement is decided solely by Amazon and is
                not a condition of purchase).
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Reinstatement clarity */}
      <section className="mt-12">
        <div className="bg-[#F04632]/5 border border-[#F04632]/30 rounded-xl p-6 flex gap-4 items-start">
          <AlertTriangle
            className="w-6 h-6 text-[#F04632] flex-shrink-0 mt-0.5"
            strokeWidth={2.5}
          />
          <div>
            <p className="text-white font-bold mb-2">
              Important — Reinstatement is not guaranteed.
            </p>
            <p className="text-white/70 text-sm sm:text-base leading-relaxed">
              We produce a professionally engineered Plan of Action based on the information
              you provide and our proprietary methodology. Whether Amazon reinstates your
              account is Amazon&apos;s decision alone. A rejected appeal is not grounds for
              refund under this policy.
            </p>
          </div>
        </div>
      </section>

      {/* How to request */}
      <section className="mt-12">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mb-6">
          How to request a refund
        </h2>
        <ol className="space-y-4">
          {[
            {
              n: "01",
              t: "Confirm eligibility",
              d: "You purchased but have not yet submitted case details or triggered generation of your Plan of Action.",
            },
            {
              n: "02",
              t: "Email us",
              d: "Send a message to hello@protegoos.com from the email address used at checkout. Include your Stripe order/receipt number if available.",
            },
            {
              n: "03",
              t: "We process the refund",
              d: "Once verified, we issue a full refund to the original payment method. Refunds typically appear in 5–10 business days depending on your bank.",
            },
          ].map((s) => (
            <li
              key={s.n}
              className="grid grid-cols-[auto_1fr] gap-4 items-start bg-[#121316] border border-white/10 rounded-xl p-5"
            >
              <span className="text-3xl font-black tracking-tighter text-[#F04632]/40 leading-none">
                {s.n}
              </span>
              <div>
                <p className="text-white font-bold text-lg leading-tight mb-1">{s.t}</p>
                <p className="text-white/70 text-sm sm:text-base leading-relaxed">{s.d}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* Contact */}
      <section className="mt-12">
        <div className="bg-[#121316] border border-white/10 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#F04632]/10 border border-[#F04632]/30 flex items-center justify-center">
              <Mail className="w-5 h-5 text-[#F04632]" strokeWidth={2.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-white/50 font-bold">
                Refund inquiries
              </p>
              <p className="font-mono text-white text-lg font-bold">hello@protegoos.com</p>
            </div>
          </div>
          <a
            href="mailto:hello@protegoos.com"
            data-testid="refund-email-cta"
            className="inline-flex items-center gap-2 px-5 py-3 bg-[#F04632] hover:bg-[#D93D2A] text-white text-sm font-bold rounded-lg uppercase tracking-widest transition-colors"
          >
            <Mail className="w-4 h-4" strokeWidth={2.5} />
            Email us
          </a>
        </div>
      </section>
    </LegalLayout>
  );
}
